import { uiText } from '../../i18n/uiText';
import { checklistOptionLabel, checklistQuestionLabel } from '../../utils/checklistOptionLabel';
import React, { useEffect, useRef, useState } from 'react';
import AttachmentItem from '../common/AttachmentItem';
import { canAnswerAuditQuestion, isAuditeeRole } from '../../utils/permissions';
import { AlertTriangle, BookOpen, ChevronDown, Paperclip, PenLine, Sparkles, Trash2 } from 'lucide-react';
import { TextArea, TextInput, SelectInput } from '../common/FormField';
import { checklistKey } from '../../utils/checklistNavigation';

const QUESTION_TYPES = {
  text: uiText("Текст"),
  yesno: uiText("Да/Нет"),
  number: uiText("Число"),
  select: uiText("Список"),
  photo: uiText("Файл / фото"),
  signature: uiText("Подпись"),
};

const SCOPE_LABELS = {
  auditor: uiText("Для аудитора"),
  customer: uiText("Для аудируемого"),
  both: uiText("Оба"),
};

const ACCEPTED_FILES = '.jpg,.jpeg,.png,.webp,.gif,.bmp,.svg,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ods,.ppt,.pptx,.zip,.rar,.7z';

export default function ChecklistSection({
  section,
  visibleKeys = null,
  focusTarget = null,
  navigationKey = null,
  onNextUnanswered,
  onBackToTools,
  hasUnanswered = false,
  answers,
  onAnswerChange,
  canEdit,
  currentUserRole,
  onFileUpload,
  onFileDelete,
  canDeleteFile = () => false,
  onRemoveQuestion,
  removeQuestionDisabled = false,
  auditFiles = [],
  onChangeQuestionScope,
  canChangeScope,
  uploadProgress = {},
  onCreateIssue,
  expectedAnswers = {},
  canManageExpectedAnswers = false,
  guidanceReadOnly = false,
  selfAudit = false,
  renderDiscussion,
  renderTechnicalRecords,
  onExpectedAnswerChange,
  onExpectedAnswerSave,
  onGenerateExpectedAnswer,
  expectedAnswerDisabled = false,
  // FIX: new prop — Set of frontend_keys for required-but-unanswered questions to highlight
  missingRequiredKeys = new Set(),
}) {
  const fileRefs = useRef({});
  const [expanded, setExpanded] = useState(true);
  const sectionRef = useRef(null);
  const containsTarget = section.questions.some(q=>checklistKey(q)===focusTarget?.key);
  useEffect(()=>{ if(containsTarget) setExpanded(true); },[containsTarget,focusTarget]);
  useEffect(()=>{
    if(!expanded || !containsTarget) return;
    const frame=requestAnimationFrame(()=>{
      const row=Array.from(sectionRef.current?.querySelectorAll('[data-question-key]') || []).find(el=>el.dataset.questionKey===focusTarget.key);
      if(row){row.scrollIntoView({block:'center',behavior:'auto'});row.focus({preventScroll:true});}
    });
    return ()=>cancelAnimationFrame(frame);
  },[expanded,containsTarget,focusTarget]);
  const isAuditee = isAuditeeRole(currentUserRole);
  const getQuestionFiles = (frontendKey) => auditFiles.filter(f => f.frontend_key === frontendKey);

  const renderUploadBox = (key, q, userCanAnswer, label) => {
    const progress = uploadProgress[checklistKey(q)] || 0;
    return (
      <div style={{ marginTop: 10 }}>
        <input ref={el => { fileRefs.current[key] = el; }} type="file" accept={ACCEPTED_FILES} style={{ display: 'none' }} onChange={e => {
          const file = e.target.files?.[0];
          if (file && onFileUpload) onFileUpload(q, file);
          e.target.value = '';
        }} />
        <button type="button" className="ap-button ap-button--secondary ap-upload-trigger" disabled={!userCanAnswer || (progress > 0 && progress < 100)} onClick={() => fileRefs.current[key]?.click()}>
          <Paperclip size={15} aria-hidden="true" /> {userCanAnswer ? label : uiText("Вопрос не адресован вам")}
        </button>
        {progress > 0 && progress < 100 && (
          <div style={{ marginTop: 8 }}>
            <progress className="ap-upload-progress" value={progress} max={100} aria-label={uiText("Загрузка файла")} />
            <div className="ap-assignee__detail">{uiText("Загрузка: ")}{progress}%</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={sectionRef} hidden={visibleKeys && !section.questions.some(q=>visibleKeys.has(checklistKey(q)))} className={`card checklist-section-card${expanded ? ' is-expanded' : ' is-collapsed'}${isAuditee ? ' auditee-checklist-section' : ''}`} style={{ marginBottom: '12px' }}>
      <button
        type="button"
        className="ap-checklist-section-toggle"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <span className="checklist-section-heading">
          <span className="card-title">{section.title}</span>
          <span className="checklist-section-count">{section.questions.length}{uiText(" вопросов")}</span>
        </span>
        <ChevronDown size={18} className="checklist-section-chevron" aria-hidden="true" />
      </button>
      {expanded ? <div className="card-body checklist-section-body" style={{ padding: '0' }}>
        {section.questions.map((q, i) => {
          const key = q.frontend_key || (q.extra_question_id ? `extra-${q.extra_question_id}` : `q-${q.question_id || q.id}`);
          const ans = answers[key] || {};
          const expectation = expectedAnswers[key] || {};
          const questionFiles = getQuestionFiles(key);
          const scope = q.effective_answer_scope || q.answer_scope || 'auditor';
          const userCanAnswer = canAnswerAuditQuestion(currentUserRole, scope, canEdit);
          const addressedToAuditee = scope === 'customer' || scope === 'both';
          const changePayloadId = q.question_id || q.id;
          const canUploadAttachment = ['photo', 'file'].includes(q.question_type) || q.allow_photo;

          // FIX: highlight this question row if it's in the missing required keys set
          const isMissingRequired = missingRequiredKeys.has(key);
          const yesSelected = ans.value_bool === true;
          const noSelected = ans.value_bool === false;

          return (
            <div
              className={`checklist-question-row${isMissingRequired ? ' is-missing-required' : ''}`}
              key={key}
              data-question-key={key}
              tabIndex={-1}
              hidden={visibleKeys && !visibleKeys.has(key)}
              style={{
                padding: '14px 20px',
                borderBottom: i < section.questions.length - 1 ? '1px solid #f0f0f0' : 'none',
                // Highlight: red left border + soft red background for unanswered required questions
                borderLeft: isMissingRequired ? '3px solid #dc2626' : '3px solid transparent',
                background: isMissingRequired ? '#fff8f8' : 'transparent',
                transition: 'background 0.2s, border-color 0.2s',
              }}
            >
              <div className="checklist-question-heading">
              <div className="checklist-label" id={`checklist-label-${key}`}>
                {i + 1}. {checklistQuestionLabel(q.text, q.qualification_rule, isAuditee)}
                {q.required && (
                  <span
                    title={isMissingRequired ? uiText("Обязательный — не заполнен") : uiText("Обязательный вопрос")}
                    style={{
                      color: isMissingRequired ? '#dc2626' : '#C0392B',
                      marginLeft: '4px',
                      fontWeight: isMissingRequired ? '700' : '400',
                    }}
                  >*</span>
                )}
                {isMissingRequired && (
                  <span className="checklist-required-missing-badge" style={{
                    marginLeft: '8px',
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#dc2626',
                    background: '#fee2e2',
                    padding: '1px 6px',
                    borderRadius: '4px',
                  }}>{uiText("Не заполнен")}</span>
                )}
                {!isAuditee ? <span style={{ marginLeft: '8px', fontSize: '10px', color: 'var(--premium-text-secondary)', fontWeight: '400' }}>[{QUESTION_TYPES[q.question_type] || q.question_type}]</span> : null}
                {!isAuditee && q.allow_comment && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#555' }}>{uiText("[+ пояснение]")}</span>}
                {!isAuditee && q.allow_photo && q.question_type !== 'photo' && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#555' }}>{uiText("[+ файл]")}</span>}
                {!isAuditee && q.is_extra && <span style={{ marginLeft: '8px', fontSize: '10px', color: '#C0392B', fontWeight: '600' }}>{uiText("[добавлен в аудите]")}</span>}
              </div>
              {currentUserRole === 'admin' && onRemoveQuestion ? (
                <button type="button" className="ap-button ap-button--secondary ap-button--sm checklist-question-remove"
                  aria-label={uiText("Удалить вопрос из этого аудита: {v0}", {v0: (q.text)})} title={uiText("Удалить вопрос только из этого аудита")}
                  onClick={() => onRemoveQuestion(q)} disabled={removeQuestionDisabled}>
                  <Trash2 size={14} aria-hidden="true" /><span>{uiText("Удалить вопрос")}</span>
                </button>
              ) : null}
              </div>

              {isAuditee ? (
                <div className={`auditee-question-access ${addressedToAuditee ? 'is-addressed' : 'is-read-only'}`}>
                  {addressedToAuditee ? uiText("Вопрос для вашей организации") : uiText("Заполняет аудитор · доступен только просмотр")}
                </div>
              ) : <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                <span className={`badge ${scope === 'customer' ? 'badge-green' : scope === 'both' ? 'badge-blue' : 'badge-gray'}`}>{SCOPE_LABELS[scope]}</span>
                {ans.answered_by && <span className="badge badge-orange">{uiText("Ответил: ")}{ans.answered_by === 'customer' ? uiText("аудируемый") : uiText("аудитор")}</span>}
                {canChangeScope && !q.is_extra && (
                  <div className="ap-scope-choices" role="group" aria-label={uiText("Кто отвечает на вопрос")}>
                    <button type="button" aria-pressed={scope === 'auditor'} className={`ap-button ap-button--sm ${scope === 'auditor' ? 'ap-button--primary' : 'ap-button--secondary'}`} onClick={() => onChangeQuestionScope(changePayloadId, 'auditor')}>{uiText("Аудитор")}</button>
                    <button type="button" aria-pressed={scope === 'customer'} className={`ap-button ap-button--sm ${scope === 'customer' ? 'ap-button--primary' : 'ap-button--secondary'}`} onClick={() => onChangeQuestionScope(changePayloadId, 'customer')}>{uiText("Аудируемый")}</button>
                    <button type="button" aria-pressed={scope === 'both'} className={`ap-button ap-button--sm ${scope === 'both' ? 'ap-button--primary' : 'ap-button--secondary'}`} onClick={() => onChangeQuestionScope(changePayloadId, 'both')}>{uiText("Оба")}</button>
                  </div>
                )}
              </div>}

              {canManageExpectedAnswers && guidanceReadOnly && expectation.expected_answer ? (
                <details className="expected-answer-field" style={{ padding: '10px 12px', marginBottom: 12 }}>
                  <summary><BookOpen size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{uiText("Подсказка: ожидаемый ответ")}</summary>
                  <p style={{ whiteSpace: 'pre-wrap', margin: '12px 0 0' }}>{expectation.expected_answer}</p>
                  {expectation.source_document?.title && <small>{uiText("Источник: ")}{expectation.source_document.title}</small>}
                </details>
              ) : canManageExpectedAnswers && !guidanceReadOnly ? (
                <div className="expected-answer-field">
                  <div className="expected-answer-field-head">
                    <span className="expected-answer-field-title"><BookOpen size={15} />{uiText(" Ожидаемый ответ")}</span>
                    <span className="expected-answer-field-note">{uiText("Внутренняя подсказка аудитора · проверяемому не показывается")}</span>
                  </div>
                  <TextArea
                    className="expected-answer-textarea"
                    aria-label={uiText("Ожидаемый ответ: {v0}", {v0: (q.text)})}
                    rows={3}
                    value={expectation.expected_answer || ''}
                    onChange={(event) => onExpectedAnswerChange?.(q, event.target.value)}
                    onBlur={() => onExpectedAnswerSave?.(q)}
                    placeholder={uiText("Опишите критерии соответствия вручную или сформируйте их по выбранному стандарту")}
                    disabled={!canEdit || expectedAnswerDisabled}
                  />
                  <div className="expected-answer-field-footer">
                    <span className="expected-answer-source">
                      {expectation.source_document?.title ? uiText("Источник: {v0}", {v0: (expectation.source_document.title)}) : uiText("Источник ещё не выбран")}
                      {expectation.saving ? uiText(" · сохранение…") : expectation.saved ? uiText(" · сохранено") : ''}
                    </span>
                    {expectation.saved === false && <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={() => onExpectedAnswerSave?.(q)} disabled={!canEdit || expectedAnswerDisabled || expectation.saving}>{uiText("Сохранить ожидаемый ответ")}</button>}
                    <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={() => onGenerateExpectedAnswer?.(q)} disabled={!canEdit || expectedAnswerDisabled || expectation.saving}>
                      <Sparkles size={14} /> {expectation.source_document_id ? uiText("Сгенерировать заново") : uiText("Сгенерировать по стандарту")}
                    </button>
                  </div>
                </div>
              ) : null}

              {q.question_type === 'yesno' && (
                <div className="checklist-answer-block">
                  <div className="yesno-btns">
                    <button
                      type="button"
                      className={`ap-button ap-button--secondary ap-answer-choice ap-answer-choice--yes${yesSelected ? ' is-selected' : ''}`}
                      onClick={() => userCanAnswer && onAnswerChange(q, 'value_bool', true)}
                      disabled={!userCanAnswer}
                      aria-pressed={yesSelected}
                    >{uiText("✓ Да")}</button>
                    <button
                      type="button"
                      className={`ap-button ap-button--secondary ap-answer-choice ap-answer-choice--no${noSelected ? ' is-selected' : ''}`}
                      onClick={() => userCanAnswer && onAnswerChange(q, 'value_bool', false)}
                      disabled={!userCanAnswer}
                      aria-pressed={noSelected}
                    >{uiText("✗ Нет")}</button>
                    {selfAudit && !isAuditee && <button type="button" className="ap-button ap-button--secondary" disabled={!userCanAnswer} onClick={() => onAnswerChange(q, 'value_bool', null)}>{uiText("Отменить ответ")}</button>}
                  </div>
                  {noSelected && onCreateIssue ? (
                    <button type="button" className="btn btn-outline btn-sm checklist-create-issue" onClick={() => onCreateIssue(q, section)}>
                      <AlertTriangle size={14} />{uiText(" Оформить замечание по этому пункту")}</button>
                  ) : null}
                </div>
              )}

              {q.question_type === 'text' && (
                <TextArea
                  aria-labelledby={`checklist-label-${key}`}
                  aria-invalid={isMissingRequired || undefined}
                  placeholder={userCanAnswer ? uiText("Введите ответ...") : uiText("Вопрос не адресован вам")}
                  value={ans.value_text || ''}
                  onChange={e => userCanAnswer && onAnswerChange(q, 'value_text', e.target.value)}
                  disabled={!userCanAnswer}
                  rows={2}
                />
              )}
              {q.question_type === 'number' && (
                <TextInput
                  type="number"
                  aria-labelledby={`checklist-label-${key}`}
                  aria-invalid={isMissingRequired || undefined}
                  placeholder="0"
                  value={ans.value_number ?? ''}
                  onChange={e => userCanAnswer && onAnswerChange(q, 'value_number', e.target.value)}
                  disabled={!userCanAnswer}
                  style={{ maxWidth: '200px' }}
                />
              )}
              {q.question_type === 'select' && (
                <SelectInput
                  aria-labelledby={`checklist-label-${key}`}
                  aria-invalid={isMissingRequired || undefined}
                  value={ans.value_text || ''}
                  onChange={e => userCanAnswer && onAnswerChange(q, 'value_text', e.target.value)}
                  disabled={!userCanAnswer}
                  style={{ maxWidth: '300px' }}
                >
                  <option value="">{uiText("Выберите вариант...")}</option>
                  {(q.options || []).map(opt => <option key={opt} value={opt}>{uiText(checklistOptionLabel(opt, q.qualification_rule, isAuditee))}</option>)}
                </SelectInput>
              )}
              {q.question_type === 'photo' && renderUploadBox(key, q, userCanAnswer, uiText("Загрузить файл / фото"))}
              {q.question_type === 'select' && q.qualification_rule?.nonconforming_options?.includes(ans.value_text) && onCreateIssue ? <button type="button" className="btn btn-outline btn-sm checklist-create-issue" onClick={() => onCreateIssue(q, section)}><AlertTriangle size={14} />{uiText(" Оформить замечание по этому критерию")}</button> : null}
              {(q.allow_comment || selfAudit) && q.question_type !== 'text' && (
                <div style={{ marginTop: 10 }}>
                  <TextArea aria-label={uiText("Поле для фиксации свидетельств: {v0}", {v0: (q.text)})} placeholder={userCanAnswer ? uiText("Свидетельства: ФИО опрошенных, документы, номера записей…") : uiText("Вопрос не адресован вам")} value={ans.comment_text || ''} onChange={e => userCanAnswer && onAnswerChange(q, 'comment_text', e.target.value)} disabled={!userCanAnswer} rows={2} />
                </div>
              )}
              {canUploadAttachment && q.question_type !== 'photo' && renderUploadBox(`${key}-attachment`, q, userCanAnswer, uiText("Приложить файл"))}
              {q.question_type === 'signature' && (
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  <TextInput aria-labelledby={`checklist-label-${key}`} aria-invalid={isMissingRequired || undefined} placeholder={userCanAnswer ? uiText("Введите ФИО подписанта или короткую подпись") : uiText("Подпись недоступна")} value={ans.value_text || ''} onChange={e => userCanAnswer && onAnswerChange(q, 'value_text', e.target.value)} disabled={!userCanAnswer} />
                  <div className="ap-save-bar">
                    <span className="workspace-inline-meta"><PenLine size={14} /> {ans.value_text ? uiText("Подписано: {v0}", {v0: (ans.value_text)}) : uiText("Подпись не указана")}</span>
                    {userCanAnswer && <button type='button' className='btn btn-outline btn-sm' onClick={() => onAnswerChange(q, 'value_text', `${isAuditee ? 'Представитель аудируемого' : 'Аудитор'} · ${new Date().toLocaleDateString('ru-RU')}`)}>{uiText("Подписать")}</button>}
                  </div>
                </div>
              )}
              {questionFiles.length > 0 && <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>{questionFiles.map(f => (
                <AttachmentItem
                  key={f.id}
                  file={f}
                  onDelete={canDeleteFile(f) && onFileDelete ? onFileDelete : null}
                  deleting={Boolean(f.deleting)}
                />
              ))}</div>}
              {renderDiscussion?.(q)}
              {renderTechnicalRecords?.(q)}
              {navigationKey === key && onBackToTools && <nav className="ap-question-navigation" aria-label={uiText("Переходы по чек-листу")}>
                <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={onBackToTools}>{uiText("К поиску и фильтрам")}</button>
                {hasUnanswered && <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={onNextUnanswered}>{uiText("К следующему без ответа")}</button>}
              </nav>}
            </div>
          );
        })}
      </div> : null}
    </div>
  );
}
